import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'
import { Edit } from '@mui/icons-material'

import { useGET_medicalRecordsReporterQuery } from 'redux/api/employeesApi'
import { useGET_medicalConditionCategoriesQuery, useGET_medicalConditionsQuery } from 'redux/api/medicalRecordsApi'
import {
  useDELETE_medicalRecordMutation,
  useGET_medicalRecordByIdQuery,
  usePATCH_medicalRecordMutation,
  usePOST_medicalRecordMutation,
} from 'redux/api/medicalRecordsApi'
import { useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'

import { Form, Formik } from 'formik'

import ImportButton from 'components/atoms/buttons/ImportButton'
import SubmitButton from 'components/atoms/buttons/SubmitButton'
import RichTextField from 'components/atoms/fields/RichTextField'
import SelectField from 'components/atoms/fields/SelectField'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/medicalRecordModel'
import { crudColorMap, crudIconMap } from 'utils/helpers'

import { ActionType, IMedicalRecordData } from 'types'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
}

const MedicalRecordForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [categoryId, setCategoryId] = useState(null)
  const backToSlug = '/medical'
  const editSlug = `/medical/edit/${id}`

  const { isLoading, entity, handleSubmit } = useCrudData({
    backToSlug,
    entityName: `${t('common.medical-records')}`,
    entityId: id,
    getEntityQuery: useGET_medicalRecordByIdQuery,
    createEntityMutation: usePOST_medicalRecordMutation,
    editEntityMutation: usePATCH_medicalRecordMutation,
    deleteEntityMutation: useDELETE_medicalRecordMutation,
  })

  const { student, condition, description, reporter, condition_category } = formModel()

  return (
    <Formik
      enableReinitialize
      initialValues={entity || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values) => {

        //console.log(values);

        const data: IMedicalRecordData = {
          student_id: values.student.id,
          condition_id: values.condition.id,
          description: values.description,
        }
        handleSubmit(action, id, data)
      }}
    >
      <Form>
        <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
          {(action === 'edit' || action === 'delete') && (
            <Grid item xs={12}>
              <SelectField
                renderChildren={false}
                fieldProps={{ ...reporter }}
                apiCall={useGET_medicalRecordsReporterQuery}
                disabled
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <SelectField
              renderChildren={false}
              fieldProps={{ ...student }}
              apiCall={useGET_studentsProfileSelectQuery}
              disabled={viewOnly}
            />
          </Grid>
        
          <Grid item xs={12}>
              <SelectField
                renderChildren={false}
                fieldProps={{ ...condition_category }}
                apiCall={useGET_medicalConditionCategoriesQuery}
                disabled={viewOnly || !!id}
                onChange={(value)=>setCategoryId(value.id)}
              />
          </Grid>
          
          <Grid item xs={12}>
              <SelectField
                renderChildren={false}
                filters={{category: categoryId}}
                fieldProps={{ ...condition }}
                apiCall={useGET_medicalConditionsQuery}
                disabled={viewOnly || !categoryId || !!id}
              />
          </Grid> 

          <Grid item xs={12}>
            <RichTextField
              fieldProps={{ ...description }}
              disabled={viewOnly}
              label={`${t('common.description')}`}
            />
          </Grid>
          { /*(action === 'edit' || (action === 'add' && entity?.id)) && ( 
            <Grid item xs={12}>
              <Box sx={{ display: 'flex' }}>
                <ImportButton />
              </Box>
            </Grid>
           ) */}
          <Grid item xs={12}>
            <Box sx={{ display: 'flex' }} className="transparent-background">
              <Button
                variant='outlined'
                color='black'
                onClick={() => navigate(backToSlug)}
                sx={{ minHeight: '2.5rem', width: '10rem' }}
              >
                <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                {`${t('common.back')}`}
              </Button>
              {action === 'view' && 
                <Box sx={{ marginLeft: 'auto' }}>
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(editSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <Edit style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.edit')}`}
                  </Button>
                </Box>
              }
              {action !== 'view' && (
                <SubmitButton
                  color={crudColorMap[action]}
                  text={`${t('form-actions.' + action)}`}
                  icon={crudIconMap[action]}
                  sx={{ width: '10rem' }}
                  isLoading={isLoading}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Form>
    </Formik>
  )
}
export default MedicalRecordForm
